/**
 * Enforces one paid copy per digital pattern.
 *
 * Every product in this store is a downloadable PDF. Adding the same variant
 * again must not increase quantity or price. The theme already tracks each
 * variant's cart quantity on its quantity-selector input, so this script caps
 * that input at one and blocks any duplicate add before the product form runs.
 */
(function () {
  'use strict';

  var BUSY_MS = 5000;
  var SELLEASY_DISCOUNT_WAIT_MS = 2500;
  var SELECTOR = '.add-to-cart-button, [ref="addToCartButton"]';
  var SELLEASY_CTA_SELECTOR = 'lb-upsell-widget .lb-bundle-cta-button [role="button"].lb-button';
  var busyVariants = new Map();
  var cartVariantQuantities = new Map();
  var cartStateReady = false;
  var selleasySync = Promise.resolve();
  var selleasyBusy = false;
  var selleasyOfferAdded = false;
  var selleasyCtaReady = false;

  function quantityInput(button) {
    var form = button && button.closest('form');
    return form ? form.querySelector('[data-cart-quantity]') : null;
  }

  function variantKey(button) {
    var form = button && button.closest('form');
    var selector = form && form.querySelector('quantity-selector-component[data-variant-id]');
    var variantId = selector && selector.getAttribute('data-variant-id');

    if (!variantId && form) {
      var idInput = form.querySelector('input[name="id"]');
      variantId = idInput && idInput.value;
    }

    return variantId || button.id || 'unknown-variant';
  }

  function cartQuantity(button) {
    var input = quantityInput(button);
    var value = input && Number(input.getAttribute('data-cart-quantity'));
    return Number.isFinite(value) ? value : 0;
  }

  function enforceOneCopyLimit(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-cart-quantity]').forEach(function (input) {
      input.setAttribute('max', '1');
      if (Number(input.value) > 1) input.value = '1';
    });
  }

  function openCartDrawer() {
    var drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  function setSelleasyCtaState(state) {
    document.querySelectorAll(SELLEASY_CTA_SELECTOR).forEach(function (button) {
      var originalLabel = button.getAttribute('data-hn-offer-label') || button.textContent.trim() || 'Claim Offer';
      if (!button.hasAttribute('data-hn-offer-label')) {
        button.setAttribute('data-hn-offer-label', originalLabel);
      }

      var stateIsCurrent =
        button.getAttribute('data-hn-offer-state') === state &&
        (state !== 'added' ||
          (button.textContent.trim() === 'Offer added' &&
            button.getAttribute('aria-disabled') === 'true' &&
            button.getAttribute('tabindex') === '-1'));
      if (stateIsCurrent) return;
      button.setAttribute('data-hn-offer-state', state);

      if (state === 'added') {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('tabindex', '-1');
        button.removeAttribute('aria-busy');
        button.style.setProperty('pointer-events', 'none', 'important');
        button.style.opacity = '0.65';
        button.textContent = 'Offer added';
        return;
      }

      button.removeAttribute('aria-disabled');
      button.setAttribute('tabindex', '0');
      button.style.removeProperty('pointer-events');
      button.style.removeProperty('opacity');
      button.textContent = originalLabel === 'Offer added' ? 'Claim Offer' : originalLabel;

      if (state === 'busy') {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });
  }

  function selectedSelleasyVariantIds() {
    return Array.from(document.querySelectorAll('lb-upsell-widget [lb-variant-id][lb-selected]'))
      .map(function (card) {
        return String(card.getAttribute('lb-variant-id') || '');
      })
      .filter(Boolean);
  }

  function updateCartVariantQuantities(items) {
    cartVariantQuantities.clear();

    (Array.isArray(items) ? items : []).forEach(function (item) {
      var variant = String(item.variant_id || item.id || '');
      var quantity = Math.max(0, Number(item.quantity) || 0);
      if (!variant || quantity < 1) return;
      cartVariantQuantities.set(variant, (cartVariantQuantities.get(variant) || 0) + quantity);
    });

    cartStateReady = true;
  }

  function updateSelleasyStateFromItems(items) {
    updateCartVariantQuantities(items);

    var offerVariants = selectedSelleasyVariantIds();
    if (!offerVariants.length) return;

    var offerIsComplete = offerVariants.every(function (variant) {
      return (cartVariantQuantities.get(variant) || 0) > 0;
    });

    selleasyOfferAdded = offerIsComplete;
    selleasyBusy = false;
    setSelleasyCtaState(offerIsComplete ? 'added' : 'idle');
  }

  function refreshSelleasyStateFromServer() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return window
      .fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart refresh failed');
        return response.json();
      })
      .then(function (cart) {
        updateSelleasyStateFromItems(cart.items);
        return trimDuplicateLines(cart);
      })
      .catch(function (error) {
        console.warn('[single-copy-guard] Could not refresh Selleasy state:', error);
      });
  }

  /**
   * The backstop. Everything else in this file guards an EVENT -- a click on an
   * add-to-cart button, or Selleasy's own `lbSECartUpdated`. Anything that
   * writes to the cart without producing one of those lands a duplicate and
   * nothing corrects it: an app that adds by its own AJAX, a second tab, a
   * retried request, `/cart/add.js` called directly. Verified on the live shop
   * on 2026-09-16 -- two direct adds gave quantity 2 and a third gave 7, with
   * every button-level guard above still in place and working.
   *
   * Order #1094 shipped two copies of one PDF that way and had to be refunded.
   * A second copy of a digital pattern is the same file, so there is no case
   * where quantity > 1 is anything but a mistake, whoever made it.
   */
  var trimming = false;

  function changeLineDirectly(root, line) {
    return window.fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.id, quantity: line.quantity }),
    });
  }

  function trimDuplicateLines(cart) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var over = items.filter(function (item) {
      return (Number(item.quantity) || 0) > 1;
    });

    if (trimming || !over.length) return Promise.resolve(false);

    trimming = true;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var actions = window.Shopify && window.Shopify.actions;
    var lines = over.map(function (item) {
      return { id: item.key || String(item.id), quantity: 1 };
    });
    var run;

    if (actions && typeof actions.updateCart === 'function') {
      // Preferred: the theme's own action, so the drawer and the cart page
      // re-render with the corrected quantity instead of showing a stale 2.
      run = actions.updateCart(
        { lines: lines },
        {
          event: {
            context: 'standard-action',
            detail: { source: 'single-copy-guard' },
          },
        }
      );
    } else {
      // Sequential, not Promise.all: two concurrent writes to the same cart
      // race and the second can be applied against a stale line key.
      run = lines.reduce(function (chain, line) {
        return chain.then(function () {
          return changeLineDirectly(root, line);
        });
      }, Promise.resolve());
    }

    function release(result) {
      trimming = false;
      return result;
    }

    return run
      .then(function () {
        return release(true);
      })
      .catch(function (error) {
        console.warn('[single-copy-guard] Could not trim duplicate lines:', error);
        return release(false);
      });
  }

  /**
   * The hooks above still wait to be TOLD the cart changed -- by a click, by a
   * `shopify:cart:lines-update`, by Selleasy's own event. Tested against the
   * live shop on 2026-09-16: two direct `/cart/add.js` calls produce none of
   * those, so the backstop never ran and the cart sat at 2, then 7. An app that
   * writes with its own AJAX is the same case.
   *
   * So watch the network instead of waiting for an announcement. This wraps
   * fetch and XHR to OBSERVE only -- the original call is passed through
   * untouched, its result and its errors are never altered -- and schedules a
   * trim once a cart-mutating request settles. Debounced, because a single add
   * often fires several cart calls in a row.
   */
  var CART_WRITE_ROUTE = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  var trimTimer = null;

  function isCartWrite(url) {
    try {
      return CART_WRITE_ROUTE.test(String(url || ''));
    } catch (_error) {
      return false;
    }
  }

  function scheduleTrim() {
    if (trimming) return;
    if (trimTimer) clearTimeout(trimTimer);
    trimTimer = setTimeout(function () {
      trimTimer = null;
      refreshSelleasyStateFromServer();
    }, 400);
  }

  (function watchCartWrites() {
    var nativeFetch = window.fetch;

    if (typeof nativeFetch === 'function') {
      window.fetch = function (input, init) {
        var url = input && input.url ? input.url : input;
        var result = nativeFetch.apply(this, arguments);

        if (isCartWrite(url) && result && typeof result.then === 'function') {
          // Schedule on both paths and swallow nothing: the caller still gets
          // the original promise, settled exactly as it would have been.
          result.then(scheduleTrim, scheduleTrim);
        }

        return result;
      };
    }

    var open = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
    if (typeof open === 'function') {
      window.XMLHttpRequest.prototype.open = function (method, url) {
        if (isCartWrite(url)) {
          this.addEventListener('loadend', scheduleTrim);
        }
        return open.apply(this, arguments);
      };
    }
  })();

  // Coming back to a tab that was left open while another tab bought something.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) scheduleTrim();
  });
  window.addEventListener('pageshow', scheduleTrim);

  function selleasyDiscountCodes() {
    try {
      var stored = JSON.parse(localStorage.getItem('lb-offer-codes') || '[]');
      return stored
        .map(function (entry) {
          return entry && entry.discount && entry.discount.code;
        })
        .filter(Boolean);
    } catch (_error) {
      return [];
    }
  }

  function hasAppliedSelleasyDiscount(cart) {
    var expectedCodes = selleasyDiscountCodes();
    var appliedCodes = cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];

    if (!expectedCodes.length) return true;

    return expectedCodes.some(function (expectedCode) {
      return appliedCodes.some(function (entry) {
        return entry && entry.applicable !== false && entry.code === expectedCode;
      });
    });
  }

  function waitForSelleasyDiscount() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var startedAt = Date.now();

    function poll() {
      return window
        .fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart refresh failed');
          return response.json();
        })
        .then(function (cart) {
          updateCartVariantQuantities(cart.items);
          if (hasAppliedSelleasyDiscount(cart) || Date.now() - startedAt >= SELLEASY_DISCOUNT_WAIT_MS) {
            return cart;
          }

          return new Promise(function (resolve) {
            setTimeout(resolve, 150);
          }).then(poll);
        });
    }

    return poll();
  }

  function stopAdd(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function releaseVariant(key) {
    var state = busyVariants.get(key);
    if (!state) return;
    busyVariants.delete(key);
    if (state.button) state.button.removeAttribute('aria-busy');
  }

  function syncSelleasyCart(cart) {
    var actions = window.Shopify && window.Shopify.actions;
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    updateCartVariantQuantities(items);

    if (!actions || typeof actions.updateCart !== 'function' || typeof actions.openCart !== 'function') {
      openCartDrawer();
      return;
    }

    var keepByVariant = new Map();

    items.forEach(function (item, index) {
      var variant = String(item.variant_id || item.id || item.key);
      var quantity = Math.max(1, Number(item.quantity) || 1);
      var finalPrice = Number(item.final_price);

      if (!Number.isFinite(finalPrice)) {
        finalPrice = Number(item.final_line_price) / quantity;
      }
      if (!Number.isFinite(finalPrice)) finalPrice = Number(item.price) || Number.MAX_SAFE_INTEGER;

      var current = keepByVariant.get(variant);
      if (!current || finalPrice < current.finalPrice) {
        keepByVariant.set(variant, { index: index, finalPrice: finalPrice });
      }
    });

    var lines = items.map(function (item, index) {
      var variant = String(item.variant_id || item.id || item.key);
      var keep = keepByVariant.get(variant);
      return {
        id: item.key || String(item.id),
        quantity: keep && keep.index === index ? 1 : 0,
      };
    });

    selleasySync = selleasySync
      .catch(function () {})
      .then(function () {
        return waitForSelleasyDiscount();
      })
      .then(function () {
        if (!lines.length) return;

        return actions.updateCart(
          { lines: lines },
          {
            event: {
              context: 'standard-action',
              detail: { source: 'selleasy' },
            },
          }
        );
      })
      .then(function () {
        selleasyOfferAdded = true;
        selleasyBusy = false;
        setSelleasyCtaState('added');
        return new Promise(function (resolve) {
          setTimeout(resolve, 150);
        }).then(function () {
          return actions.openCart();
        });
      })
      .catch(function (error) {
        selleasyBusy = false;
        setSelleasyCtaState('idle');
        console.warn('[single-copy-guard] Selleasy cart sync failed:', error);
        openCartDrawer();
      });
  }

  enforceOneCopyLimit(document);
  refreshSelleasyStateFromServer();
  document.addEventListener('DOMContentLoaded', function () {
    enforceOneCopyLimit(document);
    refreshSelleasyStateFromServer();
  });

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var selleasyButton = target.closest(SELLEASY_CTA_SELECTOR);
      if (selleasyButton) {
        if (selleasyBusy || selleasyOfferAdded) {
          stopAdd(event);
          openCartDrawer();
          return;
        }

        selleasyBusy = true;
        setSelleasyCtaState('busy');
        setTimeout(function () {
          if (selleasyOfferAdded || !selleasyBusy) return;
          selleasyBusy = false;
          setSelleasyCtaState('idle');
        }, BUSY_MS);
        return;
      }

      var button = target.closest(SELECTOR);
      if (!button) return;

      enforceOneCopyLimit(button.closest('form') || document);

      var key = variantKey(button);
      var state = busyVariants.get(key);
      var alreadyInCart = cartStateReady
        ? (cartVariantQuantities.get(String(key)) || 0) >= 1
        : cartQuantity(button) >= 1;
      var addInFlight = state && Date.now() < state.until;

      if (alreadyInCart || addInFlight) {
        stopAdd(event);
        if (alreadyInCart) openCartDrawer();
        return;
      }

      if (!cartStateReady) {
        stopAdd(event);
        button.setAttribute('aria-busy', 'true');

        refreshSelleasyStateFromServer().then(function () {
          button.removeAttribute('aria-busy');
          if (!cartStateReady) return;
          if ((cartVariantQuantities.get(String(key)) || 0) >= 1) {
            openCartDrawer();
          } else {
            button.click();
          }
        });
        return;
      }

      busyVariants.set(key, {
        button: button,
        until: Date.now() + BUSY_MS,
      });
      button.setAttribute('aria-busy', 'true');

      setTimeout(function () {
        var latest = busyVariants.get(key);
        if (latest && Date.now() >= latest.until) releaseVariant(key);
      }, BUSY_MS);
    },
    true
  );

  document.addEventListener(
    'shopify:cart:lines-update',
    function (event) {
      var settle = function (result) {
        enforceOneCopyLimit(document);
        busyVariants.forEach(function (_state, key) {
          releaseVariant(key);
        });

        var items = result && result.detail && result.detail.items;
        if (Array.isArray(items)) {
          updateSelleasyStateFromItems(items);
          trimDuplicateLines({ items: items });
        } else {
          refreshSelleasyStateFromServer();
        }
      };

      if (event.promise && typeof event.promise.then === 'function') {
        event.promise.then(settle, function () {
          busyVariants.forEach(function (_state, key) {
            releaseVariant(key);
          });
          refreshSelleasyStateFromServer();
        });
      } else {
        setTimeout(refreshSelleasyStateFromServer, 300);
      }
    },
    true
  );

  window.addEventListener('lbSECartUpdated', function (event) {
    var detail = event.detail || {};
    if (!detail.isSelleasy || !detail.isAtc || !detail.cart) return;
    syncSelleasyCart(detail.cart);
  });

  new MutationObserver(function () {
    var hasSelleasyCta = Boolean(document.querySelector(SELLEASY_CTA_SELECTOR));

    if (hasSelleasyCta && !selleasyCtaReady) {
      selleasyCtaReady = true;
      refreshSelleasyStateFromServer();
    } else if (!hasSelleasyCta) {
      selleasyCtaReady = false;
    }

    setSelleasyCtaState(selleasyBusy ? 'busy' : selleasyOfferAdded ? 'added' : 'idle');
  }).observe(document.documentElement, { childList: true, subtree: true });
})();


/* Make the Shopify Forms close action obvious without replacing its native behavior. */
(function () {
  var OFFER_DELAY_MS = 4000;
  var offerReady = false;
  var observedRoots = new WeakSet();
  var waitingHosts = new Set();

  function releaseOffers() {
    if (offerReady) return;
    offerReady = true;

    waitingHosts.forEach(function (host) {
      host.style.removeProperty('visibility');
      host.style.removeProperty('pointer-events');
      if (host.shadowRoot) fitTeaser(host, host.shadowRoot);
    });
    waitingHosts.clear();
  }

  function maybeReleaseOnScroll() {
    var scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable > 0 && window.scrollY / scrollable >= 0.35) releaseOffers();
  }

  // Measured from when the page started loading, not from when this file happened to
  // execute -- it is loaded async, so counting from here adds its own load time on top
  // and the offer lands seconds later than the number above says.
  setTimeout(releaseOffers, Math.max(0, OFFER_DELAY_MS - performance.now()));
  window.addEventListener('scroll', maybeReleaseOnScroll, { passive: true });

  function enhance(root) {
    var closeButton = root.querySelector('[aria-label="Close modal"]');
    var dialog = closeButton && closeButton.closest('[role="dialog"]');

    if (!closeButton || !dialog) return;

    var form = dialog.querySelector('form');

    if (dialog.querySelector('[data-hn-dismiss-offer]')) return;

    var dismissButton = document.createElement('button');
    dismissButton.type = 'button';
    dismissButton.setAttribute('data-hn-dismiss-offer', 'true');
    dismissButton.textContent = 'No thanks, continue shopping';
    dismissButton.style.cssText =
      'display:block;margin:12px auto 0;padding:6px 10px;border:0;background:transparent;' +
      'color:#332920;font:inherit;font-size:13px;line-height:1.4;text-decoration:underline;' +
      'text-underline-offset:3px;cursor:pointer;';

    dismissButton.addEventListener('click', function () {
      closeButton.click();
    });

    (form || dialog).appendChild(dismissButton);
  }

  /* The 15% teaser parks itself in a bottom corner, on top of the sticky add-to-cart
     bar. On a phone it covers 71% of that bar -- exactly the half holding the product
     title and the price. Stack the two instead of letting them overlap: below the
     theme's 749px breakpoint the teaser becomes a full-width strip pinned to the very
     bottom, and the add-to-cart bar rides directly above it. Desktop keeps the corner
     card and is left exactly as it was -- a full-width strip across 1400px reads as a
     cookie banner, and a bar floating 104px off the bottom reads as a bug. */
  var TEASER_STYLE_ID = 'hn-teaser-stack';
  var LIFT_STYLE_ID = 'hn-teaser-lift';

  // Shopify Forms hashes its class names, so match on the stable fragment rather than
  // the whole token -- a rebuild changes _teaserContainer_trejf_5 but not "teaserContainer".
  var TEASER_CSS =
    '@media (max-width: 749px) {' +
    '#app-embed[class*="appEmbedBottom"]{left:0!important;right:0!important;width:100%!important;' +
    'max-width:none!important;bottom:0!important;padding:0!important;}' +
    // The app lays this out as a centred grid, so a 100% width on the button resolves
    // against the auto-sized track, not the viewport. Stretch the track first.
    '#app-embed[class*="appEmbedBottom"] [class*="_container_"]{width:100%!important;' +
    'max-width:none!important;margin:0!important;grid-template-columns:1fr!important;' +
    'justify-content:stretch!important;justify-items:stretch!important;}' +
    '#app-embed[class*="appEmbedBottom"] [class*="teaserContainer"]{width:100%!important;' +
    'max-width:none!important;margin:0!important;border-radius:0!important;' +
    'padding:13px 16px!important;display:flex!important;align-items:center!important;' +
    'justify-content:center!important;gap:10px!important;}' +
    '#app-embed[class*="appEmbedBottom"] [class*="teaserHeading"]{margin:0!important;' +
    'text-align:center!important;font-size:14px!important;line-height:1.3!important;}' +
    '}';

  function styleTeaser(root) {
    if (root.getElementById && root.getElementById(TEASER_STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = TEASER_STYLE_ID;
    style.textContent = TEASER_CSS;
    root.appendChild(style);
  }

  function ensureLiftStyle() {
    if (document.getElementById(LIFT_STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = LIFT_STYLE_ID;
    style.textContent =
      '@media (max-width: 749px){' +
      'html[data-hn-teaser] .sticky-add-to-cart__bar{bottom:var(--hn-teaser-h,48px)!important;}' +
      '}';
    document.head.appendChild(style);
  }

  // Measured rather than assumed: the strip's height follows the app's own font and
  // padding, and a hard-coded offset would leave a gap or a sliver of overlap.
  function syncTeaserOffset(host, root) {
    var teaser = root.querySelector('#app-embed[class*="appEmbedBottom"]');
    // The root keeps its corner class while the full modal is open, so key the lift on
    // the teaser button itself -- otherwise the bar rides 44px high over nothing.
    var strip = root.querySelector('[class*="teaserContainer"]');
    var height = strip && teaser ? teaser.getBoundingClientRect().height : 0;
    var hidden = host.style.visibility === 'hidden';

    if (!teaser || !strip || hidden || height < 8) {
      document.documentElement.removeAttribute('data-hn-teaser');
      return;
    }

    ensureLiftStyle();
    document.documentElement.style.setProperty('--hn-teaser-h', Math.round(height) + 'px');
    document.documentElement.setAttribute('data-hn-teaser', 'on');
  }

  function fitTeaser(host, root) {
    styleTeaser(root);
    requestAnimationFrame(function () {
      syncTeaserOffset(host, root);
    });
  }

  var fittedHosts = new Set();
  window.addEventListener('resize', function () {
    fittedHosts.forEach(function (host) {
      if (host.shadowRoot) syncTeaserOffset(host, host.shadowRoot);
    });
  });

  function connect(host) {
    if (!offerReady) {
      host.style.visibility = 'hidden';
      host.style.pointerEvents = 'none';
      waitingHosts.add(host);
    } else {
      host.style.removeProperty('visibility');
      host.style.removeProperty('pointer-events');
    }

    var root = host.shadowRoot;
    if (!root) return;

    enhance(root);
    fittedHosts.add(host);
    fitTeaser(host, root);

    if (!observedRoots.has(root)) {
      new MutationObserver(function () {
        enhance(root);
        fitTeaser(host, root);
      }).observe(root, { childList: true, subtree: true });
      observedRoots.add(root);
    }
  }

  function scan() {
    document.querySelectorAll('shopify-forms-embed').forEach(connect);
  }

  scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();
/* The sticky buy bar used to hide itself once the footer came into view, which is how
   stock Horizon kept the footer clear. That was removed in assets/sticky-add-to-cart.js
   because it took the only buy button away for the whole reviews-and-footer run. The
   footer still must not end up permanently underneath the bar, so pad the footer by
   exactly the room the bar and the 15% strip occupy. Padding the footer rather than the
   body keeps the footer's own background under the reserved space instead of exposing a
   stripe of page colour below it. */
(function () {
  'use strict';

  var MOBILE = '(max-width: 749px)';
  var lastBarHeight = 0;
  var queued = false;

  function footerElement() {
    return document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
  }

  function sync() {
    queued = false;
    var footer = footerElement();
    if (!footer) return;

    var bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar || !window.matchMedia(MOBILE).matches) {
      footer.style.removeProperty('padding-bottom');
      return;
    }

    // The bar reports no height until it is stuck, so remember the last real one.
    var height = Math.round(bar.getBoundingClientRect().height);
    if (height > 0) lastBarHeight = height;

    var teaser = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--hn-teaser-h'), 10);
    var room = (lastBarHeight || 74) + (teaser > 0 ? teaser : 0);

    footer.style.setProperty('padding-bottom', room + 'px', 'important');
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(sync);
  }

  schedule();
  document.addEventListener('DOMContentLoaded', schedule);
  window.addEventListener('resize', schedule);
  window.addEventListener('scroll', schedule, { passive: true });
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-stuck'],
  });
})();
